import { Transaction } from '@apis/index'
import transaction from '@apis/transaction'
import { outlinewallet, viaapp } from '@assets/icons'
import Status from '@components/status'
import Svg from '@components/svg'
import Table from '@components/table'
import { useModal } from '@context/ModalContext'
import { RootState } from '@store/index'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { ImageIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(utc)
dayjs.extend(timezone)
import { useSelector } from 'react-redux'
import Column from '@components/column'
import DateRangeFilter from '@components/table/DateRangeFilter'
import StatusFilter from '@components/table/StatusFilter'
import styled from '@emotion/styled'
import IndividualUser from '@pages/admin/user-management/individual-user'

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
  }
`
const capitalize = (str?: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '-'
const WalletList = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [status, setStatus] = useState('')
  const { showModal } = useModal()
  const {
    items: transactions = [],
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => state.transactions)
  const [page, setPage] = useState(1)

  const fetchTransactions = async (search = '') => {
    try {
      await transaction.list({
        state: 'transactions',
        params: {
          page,
          limit: 30,
          type: 'Wallet',
          ...(search && { search }),
          ...(fromDate && { startDate: fromDate }),
          ...(toDate && { endDate: toDate }),
          ...(status && { status }),
        },
      })
    } catch (error) {
      console.error('Error fetching transactions:', error)
    }
  }

  const loadwalletcolumn = [
    {
      title: 'Created By',
      key: 'created_by',
      render: (item) => (
        <div
          onClick={() => {
            if (item?.created_by?.id) {
              showModal(<IndividualUser userStateId={item?.created_by?.id} />, {
                width: 1200,
              })
            }
          }}
        >
          {/* <button onClick={() => console.log(item)}>clic</button> */}
          {item?.created_by?.name}
        </div>
      ),
    },
    {
      title: 'Phone No.',
      key: 'phone_no',
      render: (item) => item?.created_by?.phoneNo || 'N/A',
    },
    {
      title: 'Total Points',
      key: 'total_points',
    },
    {
      title: 'Amount',
      key: 'amount',
    },
    {
      title: 'Payment Method',
      key: 'payment_method',
      render: (item: any) => {
        const paymentMethod = capitalize(item?.infos?.paymentMethod)
        return item?.infos?.image ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              justifyContent: 'center',
            }}
            onClick={() => {
              showModal(
                <div style={{ width: '100%' }}>
                  <img
                    src={`https://admin.tournaop.com/${item?.infos?.image}`}
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                    }}
                  />
                </div>,
              )
            }}
          >
            <Svg src={viaapp} />
            <span style={{ marginLeft: 8 }}>Via App</span>
          </div>
        ) : (
          <span>Via {paymentMethod}</span>
        )
      },
    },
    {
      title: 'Created Date',
      key: 'created_date',
      render: (item) => (
        <Column stylecolumn="row-gap:2px">
          <p>{item.created_date}</p>
          <p style={{ color: 'gray', fontSize: '12px' }}>{item.created_time}</p>
        </Column>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (item) => (
        <Status
          item={item}
          statuses={['Pending', 'Delivered', 'Cancelled']}
          onStatusChange={async (status) => {
            try {
              await Transaction.update({
                id: item?.id,
                state: 'transactions',
                data: { status },
              })
            } catch (err) {
              console.error('Failed to update transaction status', err)
            }
          }}
        />
      ),
    },
  ]

  const data = Array.isArray(transactions)
    ? transactions.map((t) => ({
        id: t?.id ?? 'N/A',
        created_by: t?.user ?? 'Unknown',
        total_points: t?.user?.points ?? 0,
        amount: t?.amount ?? 0,
        infos: t?.infos ?? 'N/A',
        created_date: t.createdAt ? t.createdAt.split('T')[0] : 'N/A',
        created_time: t.createdAt
          ? (() => {
              const [hour, min, sec] = t.createdAt
                .split('T')[1]
                .split('.')[0]
                .split(':')
              const h = Number(hour)
              const ampm = h >= 12 ? 'PM' : 'AM'
              const hour12 = h % 12 === 0 ? 12 : h % 12
              return `${hour12}:${min}:${sec} ${ampm}`
            })()
          : 'N/A',
        status: t?.status ?? 'N/A',
      }))
    : []
  useEffect(() => {
    setPage(1)
  }, [searchTerm, fromDate, toDate, status])
  useEffect(() => {
    fetchTransactions(searchTerm)
  }, [searchTerm, page, fromDate, toDate, status])

  return (
    <Table
      onSearchChange={(val) => {
        setSearchTerm(val)
      }}
      loading={loading}
      text="Load Wallet List"
      icon={outlinewallet}
      columns={loadwalletcolumn}
      data={data}
      searchKeys={['name', 'number', 'amount']}
      itemsPerPage={30}
      totalItems={totalItems}
      totalPages={totalPages}
      pageControlled={page}
      onPageChange={setPage}
      rightSearchElement={
        <HeaderActions>
          <DateRangeFilter
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
          <StatusFilter value={status} onChange={setStatus} />
        </HeaderActions>
      }
    />
  )
}

export default WalletList
